import { CompetenceBaseEx, CompetenceBaseTreeEx } from "@/server/backend-api";
import {
  getLevelStringFromId,
  loTypeToString,
  statusToString,
  studyFormatToString,
} from "@/server/backend-api/enumToText";
import { Audience, Tag } from "@/server/backend-api/generated";
import { OrganisationBase } from "@/server/backend-api/organisation";
import {
  addOrRemoveAudienceId,
  addOrRemoveCredit,
  addOrRemoveLearningOppStatus,
  addOrRemoveLearningOppType,
  addOrRemoveLevelId,
  addOrRemovePace,
  addOrRemoveStudyFormat,
  addOrRemoveStudyPeriodFrom,
  addOrRemoveStudyPeriodTo,
  addOrRemoveTagId,
  addOrRemoveTopicId,
  addOrRemoveUniversityId,
  LearningOpportunityFilterState,
} from "@/utils/browseFilter";
import { getCompetenceFromTreeById } from "@/utils/competenceTree";
import { format } from "date-fns";
import { IconActionClose } from "./Icons";

function FilterPill(props: { title: string; onRemove?: () => void }) {
  return (
    <button
      className="flex gap-x-1 justify-center items-center px-2 py-1 rounded-3 border border-white"
      onClick={props.onRemove}
    >
      <span className="text-style-caption">{props.title}</span>
      <IconActionClose className="w-4 h-4" />
    </button>
  );
}

export function FilterPills(props: {
  filter: LearningOpportunityFilterState;
  onFilterChange: (newFilter: LearningOpportunityFilterState) => void;
  topics: CompetenceBaseEx[];
  competenceTree: CompetenceBaseTreeEx;
  memberUniversities: OrganisationBase[];
  tags: Tag[];
  audiences: Audience[];
}) {
  const {
    filter,
    onFilterChange,
    competenceTree,
    memberUniversities,
    tags,
    audiences,
  } = props;
  const typePills = filter.selectedLearningOppTypes.map((loType) => ({
    title: loTypeToString(loType),
    onRemove: () =>
      onFilterChange(addOrRemoveLearningOppType(filter, loType, false)),
  }));
  const topicPills = filter.selectedTopicIds.map((topicId) => ({
    title: getCompetenceFromTreeById(topicId, competenceTree)?.name ?? topicId,
    onRemove: () => onFilterChange(addOrRemoveTopicId(filter, topicId, false)),
  }));
  const statusPills = filter.selectedLearningOppStatuses.map((status) => ({
    title: statusToString(status),
    onRemove: () =>
      onFilterChange(addOrRemoveLearningOppStatus(filter, status, false)),
  }));
  const studyFormatPills = filter.selectedStudyFormats.map((studyFormat) => ({
    title: studyFormatToString(studyFormat),
    onRemove: () =>
      onFilterChange(addOrRemoveStudyFormat(filter, studyFormat, false)),
  }));
  const universityPills = filter.selectedUniversityIds.map((universityId) => ({
    title:
      memberUniversities.find((x) => x.id === universityId)?.name ??
      universityId,
    onRemove: () =>
      onFilterChange(addOrRemoveUniversityId(filter, universityId, false)),
  }));
  const audiencePills = filter.selectedAudienceIds.map((audienceId) => ({
    title: audiences.find((x) => x.id === audienceId)?.name ?? audienceId,
    onRemove: () =>
      onFilterChange(addOrRemoveAudienceId(filter, audienceId, false)),
  }));
  const tagPills = filter.selectedTagIds.map((tagId) => ({
    title: tags.find((x) => x.id === tagId)?.name ?? tagId,
    onRemove: () => onFilterChange(addOrRemoveTagId(filter, tagId, false)),
  }));
  const levelPills = filter.selectedLevelIds.map((levelId) => ({
    title: getLevelStringFromId(levelId),
    onRemove: () => onFilterChange(addOrRemoveLevelId(filter, levelId, false)),
  }));

  const pacePills = filter.selectedPaces.map((pace) => ({
    title: pace,
    onRemove: () => onFilterChange(addOrRemovePace(filter, pace, false)),
  }));

  const creditPills = filter.selectedCredits.map((credit) => ({
    title: credit,
    onRemove: () => onFilterChange(addOrRemoveCredit(filter, credit, false)),
  }));

  const studyPeriodPillsFrom = filter.selectedStudyPeriodsFrom.map(
    (studyPeriod) => ({
      title: studyPeriod,
      onRemove: () =>
        onFilterChange(addOrRemoveStudyPeriodFrom(filter, studyPeriod, false)),
    })
  );
  const studyPeriodPillsTo = filter.selectedStudyPeriodsTo.map(
    (studyPeriod) => ({
      title: studyPeriod,
      onRemove: () =>
        onFilterChange(addOrRemoveStudyPeriodTo(filter, studyPeriod, false)),
    })
  );
  const pills = [
    ...typePills,
    ...topicPills,
    ...statusPills,
    ...studyFormatPills,
    ...universityPills,
    ...tagPills,
    ...levelPills,
    ...pacePills,
    ...creditPills,
    ...audiencePills,
  ];

  return (
    <div className="flex gap-y-2 gap-x-6 justify-between">
      <div className="flex flex-wrap flex- gap-x-sm gap-y-sm">
        {pills.map((pill, index) => (
          <div key={index} className="h-fit">
            <FilterPill title={pill.title} onRemove={pill.onRemove} />
          </div>
        ))}
        {filter.selectedStudyPeriodsFrom.length > 0 &&
          filter.selectedStudyPeriodsTo.length > 0 && (
            <div className="flex items-center -mt-2">
              <p className="mr-2">Study period start date between</p>
              <FilterPill
                title={format(filter.selectedStudyPeriodsFrom[0], "d MMM")}
                onRemove={studyPeriodPillsFrom[0].onRemove}
              />
              <p className="mx-2">and</p>
              <FilterPill
                title={format(filter.selectedStudyPeriodsTo[0], "d MMM")}
                onRemove={studyPeriodPillsTo[0].onRemove}
              />
            </div>
          )}
        {filter.selectedStudyPeriodsFrom.length > 0 &&
          filter.selectedStudyPeriodsTo.length === 0 && (
            <div className="flex items-center -mt-2">
              <p className="mr-2">Study period start date from</p>
              <FilterPill
                title={format(filter.selectedStudyPeriodsFrom[0], "d MMM")}
                onRemove={studyPeriodPillsFrom[0].onRemove}
              />
              <p className="mx-2">onwards</p>
            </div>
          )}
        {filter.selectedStudyPeriodsTo.length > 0 &&
          filter.selectedStudyPeriodsFrom.length === 0 && (
            <div className="flex items-center -mt-2">
              <p className="mr-2">Study Period start date from now to</p>
              <FilterPill
                title={format(filter.selectedStudyPeriodsTo[0], "d MMM")}
                onRemove={studyPeriodPillsTo[0].onRemove}
              />
            </div>
          )}
      </div>
      <button
        className="w-22 p-2 shrink-0"
        onClick={() =>
          onFilterChange({
            selectedLearningOppTypes: [],
            selectedTopicIds: [],
            selectedLearningOppStatuses: [],
            selectedUniversityIds: [],
            selectedStudyFormats: [],
            selectedTagIds: [],
            selectedLevelIds: [],
            selectedPaces: [],
            selectedCredits: [],
            selectedStudyPeriodsFrom: [],
            selectedStudyPeriodsTo: [],
            selectedAudienceIds: [],
          })
        }
      >
        Clear all
      </button>
    </div>
  );
}
